import { useAuth } from '@context/AuthContext'

/**
 * PermissionGate - Conditionally renders children based on permissions
 *
 * Usage:
 * <PermissionGate permission="assets.create">
 *   <CreateAssetButton />
 * </PermissionGate>
 *
 * <PermissionGate permissions={['assets.edit', 'assets.delete']} requireAll={false}>
 *   <EditDeleteButtons />
 * </PermissionGate>
 */
const PermissionGate = ({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  children
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = useAuth()

  if (loading) {
    return null
  }

  let hasAccess = false

  if (permission) {
    hasAccess = hasPermission(permission)
  } else if (permissions && permissions.length > 0) {
    hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions)
  }

  if (!hasAccess) {
    return fallback
  }

  return children
}

/**
 * AdminGate - Only renders for admin users
 */
export const AdminGate = ({ fallback = null, children }) => {
  const { isAdmin, loading } = useAuth()

  if (loading) {
    return null
  }

  if (!isAdmin) {
    return fallback
  }

  return children
}

/**
 * Hook to check permissions in components
 * Returns an object with permission check functions
 */
export const usePermissions = () => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin, permissions } = useAuth()

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    permissions,
    // Convenience methods for common checks
    canView: (module) => hasPermission(`${module}.view`),
    canCreate: (module) => hasPermission(`${module}.create`),
    canEdit: (module) => hasPermission(`${module}.edit`),
    canDelete: (module) => hasPermission(`${module}.delete`),
  }
}

export default PermissionGate
